import { useAsObservableSource, useLocalStore } from 'mobx-react-lite';

import {
  IcAddUserIcon,
  IcAppearanceIcon,
  IcAutoDelete24,
  IcBlockIcon,
  IcChangeOrderIcon,
  IcClearIcon,
  IcCloseIcon,
  IcDeleteIcon,
  IcEditIcon,
  IcLogOutIcon,
  IcPinStrokeIcon,
  IcPrivacyIcon,
  IcReportIcon,
  IcUsersIcon,
} from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';

import Tasks from 'APP/Tasks';
import { MAX_PIN_GROUPS } from 'APP/constants/group';
import { getTtlPeriodText } from 'APP/main/ChatView/ChatAutoCleanupSettings/ChatAutoCleanupSettings.utils';
import { AddParticipantsPopup } from 'APP/main/PopupManager/Popups/AddParticipantsPopup/AddParticipantsPopup';
import { ChatBackgroundPopup } from 'APP/main/PopupManager/Popups/ChatBackgroundPopup/ChatBackgroundPopup';
import { E2eeDisclaimerPopup } from 'APP/main/PopupManager/Popups/E2eeDisclaimerPopup/E2eeDisclaimerPopup';
import { EditChatGroupPopup } from 'APP/main/PopupManager/Popups/EditChatGroupPopup/EditChatGroupPopup';
import { GroupBlackListPopup } from 'APP/main/PopupManager/Popups/GroupBlackListPopup/GroupBlackListPopup';
import { GroupPermissionsPopup } from 'APP/main/PopupManager/Popups/GroupPermissionsPopup/GroupPermissionsPopup';
import { ParticipantsPopup } from 'APP/main/PopupManager/Popups/ParticipantsPopup/ParticipantsPopup';
import { ReportGroupPopup } from 'APP/main/PopupManager/Popups/ReportGroupPopup/ReportGroupPopup';
import { useMoveGroupToSpace } from 'APP/main/hooks/space/useMoveGroupToSpace';
import { GroupType } from 'APP/model/group/groupModel.types';
import { Features } from 'APP/packages/features/Features';
import Entities from 'STORE';

import useSharedDataItems from '../hooks/sharedDataItems';

export const useGroupProfilePresenter = ({ groupId, actions }) => {
  const { t } = useTranslation();
  const sharedDataItems = useSharedDataItems({ groupId, actions });
  const source = useAsObservableSource({ groupId });
  const moveGroupToSpace = useMoveGroupToSpace(groupId);

  const presenter = useLocalStore(() => ({
    get group() {
      return Entities.GroupsStore.getGroupById(source.groupId);
    },

    onLearnMoreClick() {
      Tasks.app.openPopup(E2eeDisclaimerPopup);
    },

    get showSecretChatSubtitle() {
      return presenter.group.isSecretGroup;
    },

    get isSecretGroup() {
      return presenter.group.type === GroupType.SecretGroup;
    },

    get verified() {
      return presenter.group.verified;
    },

    get space() {
      const spaceId = presenter.group.spaceId;

      return Features.isSpacesEnabled() && spaceId ? Entities.spacesStore.getById(spaceId) : null;
    },

    get title() {
      return presenter.group.avatarTitle;
    },

    get subtitle() {
      return t('amount_of_members_in_a_group_many').replace('{0}', presenter.group.usersCount);
    },

    get description() {
      return presenter.group.description;
    },

    get isShowInviteLink() {
      if (presenter.group.isSecretGroup) {
        return false;
      }
      if (presenter.group.isOpen) {
        return true;
      }

      return presenter.group.canInviteUsers;
    },

    get childPagesList() {
      return [
        {
          key: 'group-members',
          Icon: IcUsersIcon,
          title: t('group_chat_people_list_button'),
          onClick: presenter.openGroupMembers,
          count: presenter.group.usersCount,
          isVisible: presenter.group.canDisplayMembers,
        },
        ...sharedDataItems.items,
      ].filter((item) => item.isVisible);
    },

    openGroupMembers() {
      Tasks.app.openPopup(ParticipantsPopup, { params: { groupId: source.groupId } });
    },

    get actions() {
      const items = [
        {
          key: 'blacklist',
          isVisible: presenter.isVisibleBlacklist,
          Icon: IcBlockIcon,
          title: t('balck_list_title'),
          onClick: presenter.showBlacklist,
        },
        {
          key: 'permissions',
          isVisible: presenter.isVisiblePermissions,
          Icon: IcPrivacyIcon,
          title: t('permissions_label'),
          onClick: presenter.showPermissions,
        },
      ];

      return items.filter((item) => item.isVisible);
    },

    get isVisibleBlacklist() {
      if (!Features.isBanUserEnabled()) {
        return false;
      }
      return presenter.group.withMeInAdmins;
    },

    showBlacklist() {
      presenter.childPopup = Tasks.app.openPopup(GroupBlackListPopup, {
        params: { groupId: presenter.group.id },
      });
    },

    get isVisiblePermissions() {
      if (!Features.isPermissionsGroupEnabled()) {
        return false;
      }

      return presenter.group.withMeInAdmins;
    },

    showPermissions() {
      presenter.childPopup = Tasks.app.openPopup(GroupPermissionsPopup, {
        params: { groupId: presenter.group.id },
      });
    },

    get actionItems() {
      const items = [
        {
          key: 'add-people',
          isVisible: presenter.canAddPeople,
          Icon: IcAddUserIcon,
          title: t('group_chat_add_people_button'),
          onClick: presenter.addPeople,
        },
        {
          key: 'pin-group',
          isVisible: presenter.canPinGroup,
          Icon: IcPinStrokeIcon,
          title: t('group_chat_pin'),
          onClick: presenter.pinGroup,
        },
        {
          key: 'unpin-group',
          isVisible: presenter.canUnpinGroup,
          Icon: IcPinStrokeIcon,
          title: t('group_chat_unpin'),
          onClick: presenter.unpinGroup,
        },
        {
          key: 'edit-group',
          isVisible: presenter.canEditGroup,
          Icon: IcEditIcon,
          title: t('edit_group_chat_profile_title'),
          onClick: presenter.editGroup,
        },
        {
          key: 'chat-appearance',
          isVisible: true,
          Icon: IcAppearanceIcon,
          title: t('chat_appearance'),
          onClick: presenter.openAppearance,
        },
        {
          key: 'move-to-space',
          isVisible: presenter.canMoveToSpace,
          Icon: IcChangeOrderIcon,
          title: t('space_moveto_space_button'),
          onClick: presenter.moveToSpace,
        },
        {
          key: 'remove-from-space',
          isVisible: presenter.canRemoveFromSpace,
          Icon: IcCloseIcon,
          title: t('space_remove_from_button', { 0: presenter.space?.name }),
          onClick: presenter.removeFromSpace,
        },
        {
          key: 'report-group',
          isVisible: presenter.canReportGroup,
          Icon: IcReportIcon,
          title: t('common_report'),
          onClick: presenter.reportGroup,
        },
        {
          key: 'clear-messages',
          isVisible: presenter.canClearHistory,
          Icon: IcClearIcon,
          title: t('profile_clear_messages'),
          onClick: presenter.clearHistory,
        },
        {
          key: 'auto-delete-messages',
          isVisible: presenter.canUseAutoCleanup,
          Icon: IcAutoDelete24,
          title: t('chat_auto_cleanup_label'),
          onClick: () => Tasks.group.openAutoCleanupSettings(presenter.group?.id || ''),
          value: getTtlPeriodText(presenter.group?.ttlPeriodMs ?? 0),
        },
        {
          key: 'delete-group',
          isVisible: presenter.canDeleteGroup,
          Icon: IcDeleteIcon,
          title: t('delete_group'),
          onClick: presenter.deleteGroup,
        },
        {
          key: 'leave-group',
          isVisible: presenter.canLeaveGroup,
          Icon: IcLogOutIcon,
          title: t('leave_group_chat'),
          onClick: presenter.leaveGroup,
        },
      ];

      return items.filter((item) => item.isVisible);
    },

    get canAddPeople() {
      if (!presenter.group) {
        return false;
      }

      return presenter.group.canInviteUsers;
    },

    addPeople() {
      Tasks.app.openPopup(AddParticipantsPopup, { params: { groupId: source.groupId } });
    },

    get canPinMore() {
      return Entities.GroupsStore.pinnedChatGroups.length < MAX_PIN_GROUPS;
    },

    get canPinGroup() {
      if (!presenter.group || presenter.group.withoutMe) {
        return false;
      }

      return presenter.canPinMore && !presenter.group.isPinned;
    },

    pinGroup() {
      if (!presenter.group) {
        return;
      }

      Tasks.group.pinGroup({ groupId: presenter.group.id });
    },

    get canUnpinGroup() {
      if (!presenter.group || presenter.group.withoutMe) {
        return false;
      }

      return presenter.group.isPinned;
    },

    unpinGroup() {
      if (!presenter.group) {
        return;
      }

      Tasks.group.unpinGroup({ groupId: presenter.group.id });
    },

    get canEditGroup() {
      if (!presenter.group) {
        return false;
      }

      return presenter.group.isChatGroup && presenter.group.withMeInAdmins;
    },

    editGroup() {
      Tasks.app.openPopup(EditChatGroupPopup, {
        params: { groupId: source.groupId },
      });
    },

    openAppearance() {
      if (!presenter.group) {
        return;
      }

      Tasks.app.openPopup(ChatBackgroundPopup);
    },

    get canMoveToSpace() {
      return moveGroupToSpace.canMoveToSpace;
    },

    moveToSpace() {
      moveGroupToSpace.moveToSpace();
    },

    get canRemoveFromSpace() {
      return moveGroupToSpace.canRemoveFromSpace;
    },

    removeFromSpace() {
      moveGroupToSpace.removeFromSpace();
    },

    get canReportGroup() {
      if (!Features.isReportsEnabled()) {
        return false;
      }

      if (!presenter.group) {
        return false;
      }

      return !presenter.group.isOwner && presenter.group.isChatGroup;
    },

    reportGroup() {
      Tasks.app.openPopup(ReportGroupPopup, {
        params: { groupId: presenter.group.id },
      });
    },

    get canClearHistory() {
      if (!presenter.group) {
        return false;
      }

      return presenter.group.isOwner;
    },

    async clearHistory() {
      if (!presenter.group || presenter.group.withoutMe) {
        return;
      }

      if (presenter.group.isChatGroup && presenter.group.isOwner) {
        const { isConfirmed, isChecked = false } = await Tasks.app.showConfirmWithCheckbox({
          title: t('group_chat_clear_history_alert_headline', { 0: presenter.group.name }),
          text: t('chat_details_clear_history_message'),
          checkBoxText: t('common_clear_history_for_all_members'),
        });
        if (isConfirmed) {
          Tasks.group.clearHistory({
            groupId: presenter.group.id,
            isClearForAll: isChecked,
          });
        }
      } else {
        const isConfirmed = await Tasks.app.showConfirm({
          text: t('group_chat_clear_history_alert'),
        });

        if (isConfirmed) {
          Tasks.group.clearHistory({
            groupId: presenter.group.id,
          });
        }
      }
    },

    get canUseAutoCleanup() {
      return !!presenter.group;
    },

    get canDeleteGroup() {
      if (!presenter.group) {
        return false;
      }

      return presenter.group.isChatGroup && presenter.group.isOwner;
    },

    deleteGroup() {
      Tasks.app.showConfirm({
        text: t('delete_group_chat_alert'),
        async onSuccess() {
          await Tasks.group.deleteGroup({
            groupId: presenter.group.id,
          });
        },
      });
    },

    get canLeaveGroup() {
      if (!presenter.group || presenter.group.withoutMe) {
        return false;
      }

      return (
        !presenter.group.isOwner && presenter.group.isChatGroup && presenter.group.usersCount > 1
      );
    },

    leaveGroup() {
      if (!presenter.group) {
        return;
      }

      Tasks.app.showConfirm({
        text: t('leave_group_chat_alert'),
        async onSuccess() {
          await Tasks.group.leaveGroup({
            groupId: presenter.group.id,
          });
        },
      });
    },
  }));

  return presenter;
};
